import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DATE_OPERATOR_LABEL_KEYS,
  OPERATOR_LABEL_KEYS,
} from "@components/reports/adhoc/StepFilters";
import { FilterOperator } from "@services/reportEngine";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

import Modal from "@components/layout/Modal";
import Pagination from "@components/layout/Pagination";
import Table from "@components/layout/Table";
import Label from "@components/form/Label";
import SearchInput from "@components/form/SearchInput";
import ConfirmModal from "@components/shared/ConfirmModal";
import Loading from "@components/shared/Loading";
import NewGeneralReportButton from "./NewGeneralReportButton";
import { useAppContext } from "@context/AppContext";
import { useReportCatalog } from "@hooks/query/useReportCatalog";
import { useReportTemplates } from "@hooks/query/useReportTemplates";
import { useDeleteReportTemplate } from "@hooks/mutation/useDeleteReportTemplate";
import { ReportTemplate } from "@services/reportEngine";
import { formatDateTime } from "@utils/formatDateTime";
import { toastError, toastSuccess } from "@utils/toast";
import { formatReportFilterValue } from "./formatCell";

/**
 * CR-SEQ-2026-025 §1.3.5 — the template library. Templates are created from
 * the report builder ("Save as template" on the preview step), mirroring the
 * Virginia DEQ flow where "New general report" is the entry point; this
 * panel lists, runs and manages what was saved.
 */
const ReportTemplatesPanel: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { checkPermissions, loggedInInfo } = useAppContext();
  const currentUserId = loggedInInfo?.userDetails?.id;
  const canGenerate = checkPermissions(["GenerateReports"]);

  const [name, setName] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  // API pages from one; Pagination is zero-indexed (it renders index + 1).
  const [page, setPage] = useState(1);
  const [deleting, setDeleting] = useState<ReportTemplate | null>(null);
  const [viewing, setViewing] = useState<ReportTemplate | null>(null);

  const { data, isLoading, isError, refetch } = useReportTemplates({
    name: name || undefined,
    createdBy: createdBy || undefined,
    createdByUserId: showOnlyMine ? currentUserId : undefined,
    page,
    pageLimit: 20,
  });
  const deleteTemplate = useDeleteReportTemplate();
  const { data: catalog = [] } = useReportCatalog();

  // Labels for the View summary come from the live catalogue.
  const fieldLabel = (fieldName: string) => {
    for (const level of catalog) {
      const field = level.fields.find(own => own.name === fieldName);
      if (field) return t(field.label);
    }
    return fieldName;
  };

  const canManage = (template: ReportTemplate) =>
    canGenerate &&
    (checkPermissions(["SuperAdmin"]) ||
      (template.createdByUserId !== null &&
        template.createdByUserId === currentUserId));

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteTemplate.mutateAsync(deleting.id);
      toastSuccess(t("reporting.template_deleted"));
    } catch {
      toastError(t("reporting.template_delete_failed"));
    }
    setDeleting(null);
  };

  const rows = (data?.reportTemplates ?? []).map(template => ({
    id: template.id,
    name: (
      <span>
        {template.name}
        {!template.catalogStatus.isRunnable && (
          <span
            className="ml-2 text-xs text-amber-600"
            title={[
              ...template.catalogStatus.missing.dataLevels,
              ...template.catalogStatus.missing.fields,
              ...template.catalogStatus.missing.filterFields,
            ].join(", ")}
          >
            {t("reporting.template_needs_attention")}
          </span>
        )}
      </span>
    ),
    createdBy: template.createdBy?.name ?? t("reporting.system_template"),
    lastRunAt: template.lastRunAt
      ? formatDateTime(new Date(template.lastRunAt))
      : "-",
    action: (
      <Select
        placeholder={t("common.actions")}
        options={[
          {
            label: t("common.view"),
            value: "view",
            onSelect: () => setViewing(template),
          },
          ...(canManage(template)
            ? [
                {
                  label: t("common.edit"),
                  value: "edit",
                  onSelect: () =>
                    navigate(
                      `/polestar/reports/adhoc/new?editTemplate=${template.id}`,
                    ),
                },
                {
                  label: t("common.delete"),
                  value: "delete",
                  onSelect: () => setDeleting(template),
                },
              ]
            : []),
          // A template naming a field the catalogue no longer carries cannot
          // run; offering Run would only produce a 422. View lists what is
          // missing, Edit rebuilds it.
          ...(template.catalogStatus.isRunnable
            ? [
                {
                  label: t("reporting.run_template"),
                  value: "run",
                  onSelect: () =>
                    navigate(
                      `/polestar/reports/adhoc/templates/${template.id}/run`,
                    ),
                },
              ]
            : []),
        ]}
        onChange={(option: any) => option?.onSelect?.()}
        menuPortalTarget={document.body}
        isSearchable={false}
        value={null}
      />
    ),
  }));

  return (
    <div className="flex flex-col gap-4 grow p-4">
      <div className="flex flex-col-reverse gap-4 md:flex-row md:justify-between md:items-end">
        <form
          className="flex flex-wrap gap-4 items-end"
          onSubmit={event => event.preventDefault()}
        >
          <div>
            <Label htmlFor="template-name">
              {t("reporting.search_template_name")}
            </Label>
            <SearchInput
              id="template-name"
              value={name}
              onChange={e => {
                setName(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <Label htmlFor="template-creator">
              {t("reporting.search_template_creator")}
            </Label>
            <SearchInput
              id="template-creator"
              value={createdBy}
              onChange={e => {
                setCreatedBy(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <label
            className="flex items-center gap-2 pb-2 text-sm"
            htmlFor="show-only-mine"
          >
            <input
              id="show-only-mine"
              type="checkbox"
              checked={showOnlyMine}
              onChange={e => {
                setShowOnlyMine(e.target.checked);
                setPage(1);
              }}
            />
            {t("reporting.show_only_mine")}
          </label>
        </form>
        <NewGeneralReportButton />
      </div>

      {isLoading ? (
        <Loading />
      ) : isError ? (
        <div className="text-sm text-red-700">
          <p>{t("reporting.templates_load_failed")}</p>
          <button
            type="button"
            className="btn-default mt-3"
            onClick={() => refetch()}
          >
            {t("common.refresh")}
          </button>
        </div>
      ) : (
        <Table
          fields={[
            { title: t("common.name"), name: "name" },
            { title: t("reporting.template_creator"), name: "createdBy" },
            { title: t("reporting.template_last_run"), name: "lastRunAt" },
            { title: t("common.actions"), name: "action" },
          ]}
          data={rows}
          noRowsText={t(
            name || createdBy || showOnlyMine
              ? "reporting.no_templates_match"
              : "reporting.no_templates",
          )}
        />
      )}

      {data && (data.meta?.lastPage ?? 1) > 1 && (
        <Pagination
          pageNo={page - 1}
          lastPage={data.meta.lastPage}
          onPageChange={index => setPage(index + 1)}
        />
      )}

      <Modal open={Boolean(viewing)}>
        <div className="flex flex-col">
          <div className="border-b p-4">
            <h3 className="text-lg font-medium">{viewing?.name}</h3>
          </div>
          <div className="p-4">
            <dl className="space-y-2 text-sm">
              <div className="flex gap-2">
                <dt className="font-medium">
                  {t("reporting.template_creator")}:
                </dt>
                <dd className="text-gray-500">
                  {viewing?.createdBy?.name ?? t("reporting.system_template")}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="font-medium">
                  {t("reporting.template_last_run")}:
                </dt>
                <dd className="text-gray-500">
                  {viewing?.lastRunAt
                    ? formatDateTime(new Date(viewing.lastRunAt))
                    : "-"}
                </dd>
              </div>
              {viewing && !viewing.catalogStatus.isRunnable && (
                <div className="flex gap-2">
                  <dt className="font-medium text-amber-600">
                    {t("reporting.template_missing_items")}:
                  </dt>
                  <dd className="text-amber-600">
                    {[
                      ...viewing.catalogStatus.missing.dataLevels,
                      ...viewing.catalogStatus.missing.fields,
                      ...viewing.catalogStatus.missing.filterFields,
                    ].join(", ")}
                  </dd>
                </div>
              )}
              <div className="flex gap-2">
                <dt className="font-medium">
                  {t("reporting.summary_columns")}:
                </dt>
                <dd className="text-gray-500">
                  {viewing?.config.fields.map(fieldLabel).join(", ")}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="font-medium">
                  {t("reporting.summary_filters")}:
                </dt>
                <dd className="text-gray-500">
                  {viewing?.config.filters.length
                    ? viewing.config.filters
                        .map(
                          filter =>
                            // With its operator — "Usage: 100" reads as
                            // equality whatever the template stored.
                            `${fieldLabel(filter.field)} ${t(
                              (catalog
                                .flatMap(level => level.fields)
                                .find(field => field.name === filter.field)
                                ?.dataType === "date"
                                ? DATE_OPERATOR_LABEL_KEYS[
                                    filter.operator as FilterOperator
                                  ]
                                : undefined) ??
                                OPERATOR_LABEL_KEYS[
                                  filter.operator as FilterOperator
                                ] ??
                                filter.operator,
                            )} ${formatReportFilterValue(
                              filter.value,
                              catalog
                                .flatMap(level => level.fields)
                                .find(field => field.name === filter.field)
                                ?.dataType,
                            )}`,
                        )
                        .join(", ")
                    : "-"}
                </dd>
              </div>
            </dl>
          </div>
          <div className="border-t px-4 py-2 flex flex-row-reverse">
            <button
              type="button"
              className="btn-default"
              onClick={() => setViewing(null)}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={Boolean(deleting)}
        onConfirm={handleDelete}
        onClose={() => setDeleting(null)}
        confirmText={t("common.delete")}
        isSubmitting={deleteTemplate.isLoading}
      >
        {t("reporting.delete_template_body", { name: deleting?.name ?? "" })}
      </ConfirmModal>
    </div>
  );
};

export default ReportTemplatesPanel;
